import { useEffect, useRef, useState } from "react";
import type { PortfolioItem } from "../models/PortfolioItem.js";
import { portfolioItems } from "../portfolio/items.js";

/** True when the visitor asked the OS for reduced motion. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/** MP4 spin: autoplay muted loop with poster; under reduced motion shows the poster with manual controls, no autoplay. */
function SpinTile({ item }: { item: PortfolioItem }) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (reduced) ref.current?.pause();
  }, [reduced]);
  return (
    <video
      ref={ref}
      className="t-portfolio-media"
      src={item.src}
      poster={item.poster}
      width={item.width}
      height={item.height}
      muted
      loop
      playsInline
      preload="metadata"
      autoPlay={!reduced}
      controls={reduced}
      aria-label={item.alt}
      data-reduced-motion={reduced ? "true" : "false"}
    />
  );
}

function BeforeAfterTile({ item }: { item: PortfolioItem }) {
  return (
    <div className="t-before-after" data-kind="beforeAfter">
      <figure>
        <img src={item.beforeSrc} width={item.width} height={item.height} alt={`Reference photo: ${item.alt}`} loading="lazy" />
        <figcaption>Before (supplied photo)</figcaption>
      </figure>
      <figure>
        <img src={item.src} width={item.width} height={item.height} alt={`Render: ${item.alt}`} loading="lazy" />
        <figcaption>After (render)</figcaption>
      </figure>
    </div>
  );
}

function Tile({ item }: { item: PortfolioItem }) {
  if (item.isSpin()) return <SpinTile item={item} />;
  if (item.isBeforeAfter()) return <BeforeAfterTile item={item} />;
  return <img className="t-portfolio-media" src={item.src} width={item.width} height={item.height} alt={item.alt} loading="lazy" />;
}

/** Data-driven grid. Content comes from portfolio/items.ts. */
export function Portfolio({ items = portfolioItems() }: { items?: PortfolioItem[] }) {
  return (
    <section className="t-portfolio" data-section="portfolio" aria-labelledby="portfolio-title">
      <header className="t-highlights-head">
        <h2 className="t-section-title" id="portfolio-title">Portfolio</h2>
        <p className="t-section-lead">
          Self-initiated sample work on fictional and public-domain products, lit and rendered by
          us in Blender. Client work will appear here as it ships.
        </p>
      </header>
      <ul className="t-portfolio-grid">
        {items.map((item) => (
          <li key={item.id} data-kind={item.kind} className={item.isBeforeAfter() || item.isSpin() ? "t-portfolio-wide" : undefined}>
            <figure>
              <Tile item={item} />
              <figcaption>{item.caption}</figcaption>
            </figure>
          </li>
        ))}
      </ul>
    </section>
  );
}
